import {
  runGqlQueryV2,
  gqlClient,
  gqlHeader,
  reportAsyncError,
} from "@graffer-inc/smart-apply";
import {
  ProcedureConsts,
  ProcedureEntity,
  ProcedureType,
  customUrlAllowedCharRegex,
  procedureIdFormatRegex,
} from "../../../../../application/domain/procedures/procedure-entity";
import { Auth } from "../../../../../application/domain/auth/auth";

const errorMessages = {
  InvalidPath: "半角英数字、ハイフン(-)で入力してください。",
  SameAsProcedureIdFormat: "手続きIDと同じ19桁数字の形式は使用できません。",
  TooLong: (maxLength: number) => `${maxLength}文字以内で入力してください。`,
  NotDeleteCurrentPath: "現在利用されているURLを削除することはできません。",
  SameAsCurrentPath: "現在の申請開始ページのURLと同じです。",
  PathUsedInOtherGroup: (newPath: string) =>
    `「${newPath}」は、すでに他のグループによって管理されている手続きに使用されているため、使用することができません。\n別のURLを指定してください。`,
  CannotUseReservedPath: (newPath: string, procedure: ProcedureEntity) =>
    `「${newPath}」は、現在「${procedure.name}」で予約されています。\n既に予約されているURLを使用することはできません。\n別のURLを指定してください。`,
};

const OverWriteCurrentPathMessage = (args: {
  newPath: string;
  usingPathProcName: string;
  overWriteProcName: string;
}) =>
  `「${args.newPath}」は、現在「${args.usingPathProcName}」で使用されています。\nこのまま更新すると、即座に「${args.usingPathProcName}」のURL設定が解除され「${args.overWriteProcName}」に割り当てられます。`;

const validateCustomPathString = (path: string): string | undefined => {
  if (!path.match(customUrlAllowedCharRegex)) {
    return errorMessages.InvalidPath;
  }
  if (path.match(procedureIdFormatRegex)) {
    return errorMessages.SameAsProcedureIdFormat;
  }
  if (path.length > ProcedureConsts.MAX_CUSTOM_PATH_LENGTH) {
    return errorMessages.TooLong(ProcedureConsts.MAX_CUSTOM_PATH_LENGTH);
  }
  return undefined;
};

export const validateCurrentCustomPathFormat = (
  newCurrentPath: string | undefined,
  isExistCurrentPath: boolean,
): string | undefined => {
  if (!newCurrentPath) {
    if (isExistCurrentPath) {
      return errorMessages.NotDeleteCurrentPath;
    }
    return undefined;
  }
  const error = validateCustomPathString(newCurrentPath);
  if (error) {
    return error;
  }
  return undefined;
};

export type DuplicatePathResults = {
  status: "error" | "warning";
  message: string;
};

export const validateCurrentPathDuplicateOfExistingPaths = async (args: {
  orgId: string;
  newCurrentPath: string;
  existingCurrentPath: string;
  procName: string;
  auth: Auth;
}): Promise<DuplicatePathResults | undefined> => {
  const { orgId, newCurrentPath, existingCurrentPath, procName, auth } = args;
  // 現在のカスタムパスが設定されていない場合、パスが変更されていない場合は重複チェックをスキップする
  if (!newCurrentPath || newCurrentPath === existingCurrentPath) {
    return undefined;
  }

  const { procUsingCustomPath, procHasReservedCustomPath } =
    await fetchSamePathProcs(orgId, newCurrentPath);
  if (
    (procHasReservedCustomPath &&
      !hasProcUpdatePermission(auth, procHasReservedCustomPath)) ||
    (procUsingCustomPath && !hasProcUpdatePermission(auth, procUsingCustomPath))
  ) {
    return {
      status: "error",
      message: errorMessages.PathUsedInOtherGroup(newCurrentPath),
    };
  }
  if (procHasReservedCustomPath) {
    return {
      status: "error",
      message: errorMessages.CannotUseReservedPath(
        newCurrentPath,
        procHasReservedCustomPath,
      ),
    };
  }
  if (procUsingCustomPath) {
    return {
      status: "warning",
      message: OverWriteCurrentPathMessage({
        newPath: newCurrentPath,
        usingPathProcName: procUsingCustomPath.name,
        overWriteProcName: procName,
      }),
    };
  }
  return undefined;
};

const fetchSamePathProcs = async (
  orgId: string,
  path: string,
): Promise<{
  procUsingCustomPath: ProcedureEntity | undefined;
  procHasReservedCustomPath: ProcedureEntity | undefined;
}> => {
  const result = await runGqlQueryV2(() =>
    gqlClient.proceduresWithCustomPath(
      { path: path, orgId: orgId },
      gqlHeader("q"),
    ),
  );
  if (result.error) {
    reportAsyncError(result.error.rawError);
    throw new Error(result.error.rawError.message);
  }

  const procUsingCustomPath = result.convert((e) => {
    if (!e.proceduresWithCustomPath?.procUsingCustomPath) return undefined;
    return ProcedureEntity.fromJSON(
      e.proceduresWithCustomPath?.procUsingCustomPath as ProcedureType,
    );
  });
  const procHasReservedCustomPath = result.convert((e) => {
    if (!e.proceduresWithCustomPath?.procHasReservedCustomPath)
      return undefined;
    return ProcedureEntity.fromJSON(
      e.proceduresWithCustomPath?.procHasReservedCustomPath as ProcedureType,
    );
  });
  return {
    procUsingCustomPath: procUsingCustomPath.data,
    procHasReservedCustomPath: procHasReservedCustomPath.data,
  };
};

const hasProcUpdatePermission = (
  auth: Auth,
  procedure: ProcedureEntity,
): boolean => {
  return auth.isServiceProviderAccount()
    ? !!auth.hasPermissions("procedures.update")
    : auth.checkGroupPermission(procedure, ["procedures.update"]);
};
